import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Package, Boxes, Search, RefreshCw, TrendingUp, TrendingDown, AlertTriangle, BarChart3, PlayCircle } from 'lucide-react';

const Inventory = () => {
  const { api } = useAuth();
  const [itemsInventory, setItemsInventory] = useState([]);
  const [partsInventory, setPartsInventory] = useState([]);
  const [runPartsInventory, setRunPartsInventory] = useState([]);
  const [runMaterialsInventory, setRunMaterialsInventory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [itemSearch, setItemSearch] = useState('');
  const [partSearch, setPartSearch] = useState('');
  const [runPartSearch, setRunPartSearch] = useState('');
  const [runMaterialSearch, setRunMaterialSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, partsRes, summaryRes] = await Promise.all([
        api.get('/inventory/items'),
        api.get('/inventory/parts'),
        api.get('/inventory/summary')
      ]);
      setItemsInventory(itemsRes.data);
      setPartsInventory(partsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to fetch inventory data');
    } finally {
      setLoading(false);
    }
    try { const r = await api.get('/inventory/run-parts'); setRunPartsInventory(r.data); } catch (e) {}
    try { const r = await api.get('/inventory/run-materials'); setRunMaterialsInventory(r.data); } catch (e) {}
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredItems = itemsInventory.filter(item => {
    if (!itemSearch) return true;
    const term = itemSearch.toLowerCase();
    return (item.code || '').toLowerCase().includes(term) || 
           (item.name || '').toLowerCase().includes(term) ||
           (item.item_type || '').toLowerCase().includes(term);
  });

  const filteredParts = partsInventory.filter(part => {
    if (!partSearch) return true;
    const term = partSearch.toLowerCase();
    return (part.part_no || '').toLowerCase().includes(term) || 
           (part.name || '').toLowerCase().includes(term);
  });

  const getStockStatusBadge = (stock) => {
    if (stock <= 0) {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Out of Stock</span>;
    } else if (stock < 100) {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Low Stock</span>;
    }
    return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">In Stock</span>;
  };

  // Calculate totals for items
  const itemTotals = filteredItems.reduce((acc, item) => ({
    received: acc.received + item.received,
    issued: acc.issued + item.issued,
    stock: acc.stock + item.current_stock
  }), { received: 0, issued: 0, stock: 0 });

  // Calculate totals for parts
  const partTotals = filteredParts.reduce((acc, part) => ({
    produced: acc.produced + part.total_produced,
    accepted: acc.accepted + part.accepted,
    rejected: acc.rejected + part.rejected,
    stock: acc.stock + part.current_stock
  }), { produced: 0, accepted: 0, rejected: 0, stock: 0 });

  return (
    <div className="p-6 max-w-[1600px] mx-auto animate-fade-in" data-testid="inventory">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-sky-500" />
            Inventory Report
          </h1>
          <p className="text-slate-500 text-sm">Current stock levels of items and parts</p>
        </div>
        <Button
          onClick={fetchData}
          variant="outline"
          className="rounded-sm"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Items</p>
                  <p className="text-2xl font-bold text-slate-900">{(summary.items && summary.items.total_items) || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center">
                  <Package className="w-6 h-6 text-sky-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Low Stock Items</p>
                  <p className="text-2xl font-bold text-orange-600">{(summary.items && summary.items.low_stock_count) || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Parts</p>
                  <p className="text-2xl font-bold text-slate-900">{(summary.parts && summary.parts.total_parts) || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Boxes className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Parts Produced</p>
                  <p className="text-2xl font-bold text-green-600">{((summary.parts && summary.parts.total_produced) || 0).toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Inventory Tabs */}
      <Tabs defaultValue="run-parts" className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="run-parts" className="data-[state=active]:bg-white">
            <PlayCircle className="w-4 h-4 mr-2" />
            Parts (Production Runs)
          </TabsTrigger>
          <TabsTrigger value="run-materials" className="data-[state=active]:bg-white">
            <Package className="w-4 h-4 mr-2" />
            Materials (Production Runs)
          </TabsTrigger>
          <TabsTrigger value="items" className="data-[state=active]:bg-white">
            <Package className="w-4 h-4 mr-2" />
            Items (Legacy)
          </TabsTrigger>
          <TabsTrigger value="parts" className="data-[state=active]:bg-white">
            <Boxes className="w-4 h-4 mr-2" />
            Parts (Legacy)
          </TabsTrigger>
        </TabsList>

        {/* Production Run Parts Tab */}
        <TabsContent value="run-parts">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Parts Inventory (from Production Runs)</CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Search part no, name..." value={runPartSearch}
                    onChange={(e) => setRunPartSearch(e.target.value)} className="pl-10 rounded-sm" />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
              ) : runPartsInventory.length === 0 ? (
                <div className="text-center py-12 text-slate-500">No production run data yet</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Part No</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead className="text-right">Produced</TableHead>
                        <TableHead className="text-right">Prod. Accepted</TableHead>
                        <TableHead className="text-right">Prod. Rejected</TableHead>
                        <TableHead className="text-right">Rej. Weight (g)</TableHead>
                        <TableHead className="text-right">QC Accepted</TableHead>
                        <TableHead className="text-right">QC Rejected</TableHead>
                        <TableHead className="text-right">Dispatched</TableHead>
                        <TableHead className="text-right">SO Committed</TableHead>
                        <TableHead className="text-right">Available</TableHead>
                        <TableHead>Stock</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {runPartsInventory
                        .filter(p => {
                          if (!runPartSearch) return true;
                          const t = runPartSearch.toLowerCase();
                          return (p.part_no || '').toLowerCase().includes(t) || (p.name || '').toLowerCase().includes(t);
                        })
                        .map(part => (
                          <TableRow key={part.part_id}>
                            <TableCell className="font-mono font-bold">{part.part_no}</TableCell>
                            <TableCell>{part.name}</TableCell>
                            <TableCell className="text-sm text-slate-600">{part.customer}</TableCell>
                            <TableCell className="text-right font-mono">{part.total_produced.toFixed(0)}</TableCell>
                            <TableCell className="text-right font-mono text-green-600">{part.total_accepted.toFixed(0)}</TableCell>
                            <TableCell className="text-right font-mono text-red-600">{part.total_rejected.toFixed(0)}</TableCell>
                            <TableCell className="text-right font-mono text-orange-600">{part.total_rejection_weight.toFixed(2)}</TableCell>
                            <TableCell className="text-right font-mono text-emerald-600">{(part.insp_accepted || 0).toFixed(0)}</TableCell>
                            <TableCell className="text-right font-mono text-rose-600">{(part.insp_rejected || 0).toFixed(0)}</TableCell>
                            <TableCell className="text-right font-mono text-slate-500">{part.dispatched.toFixed(0)}</TableCell>
                            <TableCell className="text-right font-mono text-amber-600">{(part.so_committed || 0).toFixed(0)}</TableCell>
                            <TableCell className="text-right font-mono font-bold text-lg">{part.available.toFixed(0)}</TableCell>
                            <TableCell>{getStockStatusBadge(part.available)}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Production Run Materials Tab */}
        <TabsContent value="run-materials">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Material Inventory (from Production Runs)</CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Search material, GRN..." value={runMaterialSearch}
                    onChange={(e) => setRunMaterialSearch(e.target.value)} className="pl-10 rounded-sm" />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
              ) : runMaterialsInventory.length === 0 ? (
                <div className="text-center py-12 text-slate-500">No material consumption data yet</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Code</TableHead>
                        <TableHead>Material</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>GRN No</TableHead>
                        <TableHead>Issue Item No</TableHead>
                        <TableHead className="text-right">GRN Received</TableHead>
                        <TableHead className="text-right">Consumed</TableHead>
                        <TableHead className="text-right">Available</TableHead>
                        <TableHead>UOM</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {runMaterialsInventory
                        .filter(m => {
                          if (!runMaterialSearch) return true;
                          const t = runMaterialSearch.toLowerCase();
                          return (m.item_code || '').toLowerCase().includes(t) ||
                            (m.item_name || '').toLowerCase().includes(t) ||
                            (m.grn_no ? `GRN-${m.grn_no}` : '').toLowerCase().includes(t);
                        })
                        .map((mat, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-mono font-bold">{mat.item_code}</TableCell>
                            <TableCell>{mat.item_name}</TableCell>
                            <TableCell>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                                {mat.item_type || '-'}
                              </span>
                            </TableCell>
                            <TableCell className="font-mono">{mat.grn_no ? `GRN-${mat.grn_no}` : '-'}</TableCell>
                            <TableCell>{mat.issue_item_no}</TableCell>
                            <TableCell className="text-right font-mono text-green-600">{mat.grn_received > 0 ? mat.grn_received.toFixed(3) : '-'}</TableCell>
                            <TableCell className="text-right font-mono text-red-600">{mat.total_consumed.toFixed(3)}</TableCell>
                            <TableCell className="text-right font-mono font-bold text-lg">
                              {mat.available !== null ? mat.available.toFixed(3) : '-'}
                            </TableCell>
                            <TableCell>{mat.uom || '-'}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Items Tab */}
        <TabsContent value="items">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Raw Material Inventory</CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search items..."
                    value={itemSearch}
                    onChange={(e) => setItemSearch(e.target.value)}
                    className="pl-10 rounded-sm"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="spinner" />
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-12 text-slate-500">No items found</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Code</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Received</TableHead>
                        <TableHead className="text-right">Issued</TableHead>
                        <TableHead className="text-right">Current Stock</TableHead>
                        <TableHead>UOM</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="font-mono font-bold">{item.code}</TableCell>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                              {item.item_type || '-'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-mono text-green-600">
                            <TrendingUp className="w-3 h-3 inline mr-1" />
                            {item.received.toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-red-600">
                            <TrendingDown className="w-3 h-3 inline mr-1" />
                            {item.issued.toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right font-mono font-bold text-lg">
                            {item.current_stock.toFixed(2)}
                          </TableCell>
                          <TableCell>{item.uom || '-'}</TableCell>
                          <TableCell>{getStockStatusBadge(item.current_stock)}</TableCell>
                        </TableRow>
                      ))}
                      {/* Totals Row */}
                      <TableRow className="bg-slate-100 font-bold">
                        <TableCell colSpan={3}>TOTAL</TableCell>
                        <TableCell className="text-right font-mono text-green-600">
                          {itemTotals.received.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-red-600">
                          {itemTotals.issued.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-lg">
                          {itemTotals.stock.toFixed(2)}
                        </TableCell>
                        <TableCell colSpan={2}></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Parts Tab */}
        <TabsContent value="parts">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Finished Goods Inventory</CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search parts..."
                    value={partSearch}
                    onChange={(e) => setPartSearch(e.target.value)}
                    className="pl-10 rounded-sm"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="spinner" />
                </div>
              ) : filteredParts.length === 0 ? (
                <div className="text-center py-12 text-slate-500">No parts found</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Part No</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead className="text-right">Produced</TableHead>
                        <TableHead className="text-right">Accepted</TableHead>
                        <TableHead className="text-right">Rejected</TableHead>
                        <TableHead className="text-right">Current Stock</TableHead>
                        <TableHead>UOM</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredParts.map((part) => (
                        <TableRow key={part.id}>
                          <TableCell className="font-mono font-bold">{part.part_no}</TableCell>
                          <TableCell>{part.name}</TableCell>
                          <TableCell className="text-right font-mono">
                            {part.total_produced.toFixed(0)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-green-600">
                            {part.accepted.toFixed(0)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-red-600">
                            {part.rejected.toFixed(0)}
                          </TableCell>
                          <TableCell className="text-right font-mono font-bold text-lg">
                            {part.current_stock.toFixed(0)}
                          </TableCell>
                          <TableCell>{part.uom || 'Nos'}</TableCell>
                          <TableCell>{getStockStatusBadge(part.current_stock)}</TableCell>
                        </TableRow>
                      ))}
                      {/* Totals Row */}
                      <TableRow className="bg-slate-100 font-bold">
                        <TableCell colSpan={2}>TOTAL</TableCell>
                        <TableCell className="text-right font-mono">
                          {partTotals.produced.toFixed(0)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-green-600">
                          {partTotals.accepted.toFixed(0)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-red-600">
                          {partTotals.rejected.toFixed(0)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-lg">
                          {partTotals.stock.toFixed(0)}
                        </TableCell>
                        <TableCell colSpan={2}></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Inventory;
